// @owners { team: patients-team }
import { SIZES, SPACING } from '@alto/design-library-tokens';
import { LgSpacing, MdSpacing } from '@alto/design-system';
import { AddOnsContext, ProgynyUnboxingGuide, useUnboxingGuide } from '@alto/features';
import React, { useMemo } from 'react';
import { ORIGINS } from '~shared/features/essentials/add_ons_consts';
import { type Prescription, type Shipment } from '~shared/types';
import { FullPage } from '~web/features/essentials/components';
import { DesktopProducts } from '~web/features/essentials/pages/OrderSuccess/DesktopProducts';
import { LastRefillCard } from '~web/features/essentials/pages/OrderSuccess/LastRefillCard';
import { MobileProducts } from '~web/features/essentials/pages/OrderSuccess/MobileProducts';
import { OrderSuccessHeader } from '~web/features/essentials/pages/OrderSuccess/OrderSuccessHeader';
import { View } from '~web/features/essentials/primitives';
import { useDeprecatedMediaQuery, useDeprecatedWindowResize } from '~web/hooks';

type Props = {
  shipment: Shipment;
  isSplitOrder: boolean;
  lastRefillPrescriptions: Prescription[];
  hasCompound: boolean;
};

export const OrderSuccessWithProducts = ({ shipment, isSplitOrder, lastRefillPrescriptions, hasCompound }: Props) => {
  const addOnsContext = useMemo(
    () => ({ shipmentID: shipment?.id, origin: ORIGINS.ORDER_SUCCESS }) as const,
    [shipment?.id],
  );
  const isMobile = !useDeprecatedMediaQuery('SM');
  const isLargeScreen = useDeprecatedMediaQuery('LG');
  const showProducts = isSplitOrder || !hasCompound;
  const showDesktopProducts = !isMobile && showProducts;
  const showMobileProducts = isMobile && showProducts;
  const screenPartitions = isMobile || !showDesktopProducts ? '100%' : '50%';
  const windowSize = useDeprecatedWindowResize();
  const { showUnboxingGuide } = useUnboxingGuide({ shipmentID: shipment?.id });

  // Define paddings
  let defaultViewSidePadding: string;
  if (!showDesktopProducts) {
    defaultViewSidePadding = '0px';
  } else if (isMobile) {
    defaultViewSidePadding = SPACING.STATIC.LG.px;
  } else {
    defaultViewSidePadding = SPACING.STATIC.XXL.px;
  }

  let sidePadding: string;
  if (!showDesktopProducts) {
    sidePadding = '0px';
  } else if (isLargeScreen) {
    sidePadding = `${(windowSize.width - parseFloat(SIZES.PAGE.WEB_NAVBAR_WIDTH.LG)) / 2}px`;
  } else {
    sidePadding = defaultViewSidePadding;
  }

  return (
    <FullPage>
      <AddOnsContext.Provider value={addOnsContext}>
        <View
          padding={`${SPACING.STATIC.XXXL.px} ${defaultViewSidePadding} ${SPACING.STATIC.XXXL.px}`}
          flexBasis={screenPartitions}
          flexDirection="column"
          backgroundColor="TERTIARY_LIGHTEST"
        >
          <View
            flexDirection="column"
            alignItems="center"
          >
            <OrderSuccessHeader
              shipment={shipment}
              isSplitOrder={isSplitOrder}
            />
            <LgSpacing />
            {showUnboxingGuide && (
              <>
                <LgSpacing />
                <ProgynyUnboxingGuide />
              </>
            )}
            {lastRefillPrescriptions.length > 0 && <LastRefillCard prescriptions={lastRefillPrescriptions} />}
            {showMobileProducts ? (
              <>
                <MdSpacing />
                {shipment ? <MobileProducts /> : null}
              </>
            ) : null}
          </View>
        </View>
        {showDesktopProducts && (
          <View
            flexDirection="column"
            padding={`${SPACING.STATIC.XXXL.px} ${sidePadding} ${SPACING.STATIC.XXXL.px} ${SPACING.STATIC.XXL.px}`}
            flexBasis={screenPartitions}
            overflow="hidden"
          >
            <DesktopProducts />
          </View>
        )}
      </AddOnsContext.Provider>
    </FullPage>
  );
};
